import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { Subscription } from '@/constants/enum';

interface IState {
  url: string;
  quoteAppInstalled: boolean;
  settings: {
    id: number;
    identifierId: string;
    category?: string;

    // VPN / Security
    vpnBlocker: boolean; // was activeVpnProxy
    vpnBlockType: string; // was typeRestrictionVpnProxy
    vpnRedirectLink?: string; // was linkRedirectVpnProxy
    vpnRiskCore: number;
    allowVpnPrivateRelay: boolean; // was allowVpnFromPrivateRelay

    // Protection Features
    protectContents: boolean;
    disableRightClick: boolean;
    disableKeyboardShortcut: boolean;
    disableInspect: boolean;
    disableDrag: boolean;
    disableSpyExtensions: boolean;

    // Bot & Analytics
    autoBlock: boolean;
    torBlock: boolean;
    showWatermark: boolean;
    createdFirstRule: boolean; // NEW
    blockUnknownBots: boolean;
    enableVisitorAnalytics: boolean;

    // Display & Welcome
    blockingTemplateId?: number;
    displayWelcome: boolean;
    welcomeStep: number;
    autoSyncRule: boolean;

    // Order Risk & Fraud
    autoBlockFraudOrders: boolean;
    orderRiskScore: number;

    hasReview: boolean;
    shopTimezone: string;
    testBlockUrl: string;
    currency: string;
    shopifyPlan: string;
    campaigns: string;
    isShowDevelopmentPlanPage: boolean;
    // ✂️ Removed fields not present in entity:
    // - tempLogoImage, tempTitle, tempDescription (likely come from blockTemplate)
    // - ip, createdAt (not defined in entity)
    // - discount, discountType (not defined in entity)
    // - shopifyPlan, shopTimezone, status, trialDaysRemaining, testBlockUrl, campaigns → migrate to ShopInfo or other source if needed

    // Relationships
    user: {
      id: number;
      identifierId: string;
      name?: string;
      otherDomain?: string;
      email?: string;
      phone?: string;
      country?: string;
      city?: string;
      zipcode?: string;
      timezone?: string;
      shoplinePlan?: string;
      shopJson: string;
      trialEndsOn?: string | Date;
      enableApp: boolean;
      oldPlan: string;
      plan: string;
      linkAccess?: string;
      linkAccessUpdatedAt?: number;
      planUpdateAt?: Date;
      siteId?: string;
      siteUrl?: string;
      subscription?: Subscription;
      installedDate: Date;
      siteName?: string;
      businessName: string;
      lastInstalledDate: string;
      // Scopes
      validationScope: boolean;
      paymentCustomizationScope: boolean;
      readContentScope: boolean;
      deliveryCustomizationScope: boolean;
      orderScope: boolean;
      showOverlayByPass: boolean;
      sendFraudOrder: boolean;
      fraudOrderEmail?: string;
      userScopes: string;
      webPixelScope: number;
      numberRuleLimit: number;
      numberVisitorLimit: number;
      isActive: boolean;
      downgrade: boolean;
      cannyId?: string;
    };

    blockTemplate?: {
      logoImage: string;
      title: string;
      titleColor: string;
      description: string;
      descriptionColor: string;
      backgroundType: string;
      background: string;
    };
  };
  summaryStats: {
    blockingRule: number;
    redirectingRule: number;
    whitelistRule: number;
  };
}
const initialState: { data: IState | null; loading: boolean } = {
  data: null,
  loading: false,
};
export const dataSettingsSlice = createSlice({
  name: 'dataSettings',
  initialState,
  reducers: {
    handleSettings: (state, action: PayloadAction<IState>) => {
      state.data = action.payload;
    },
    handleLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    handleBlockingTemplateId: (state, action: PayloadAction<number>) => {
      state.data = {
        ...state.data!,
        settings: {
          ...state.data?.settings!,
          blockingTemplateId: action.payload,
        },
      };
    },
  },
});

export const dataSettingsSelector = createSelector(
  (state: RootState) => state.dataSettings,
  (state) => state.data,
);
export const loadingSelector = createSelector(
  (state: RootState) => state.dataSettings,
  (state) => state.loading,
);

export const { handleBlockingTemplateId } = dataSettingsSlice.actions;

export default dataSettingsSlice;
